// Shared parts of the edge's protocol tabs (BACnet selection, M-Bus, KNX;
// ADR 0008): the tab bar and the bar that applies or releases a draft.
import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import type { BundleState } from './api/protocol-types.js';
import { ErrorBox } from './components.js';
import { bundleSummary, isStaleConfig, staleConfigMessage } from './lib/protocols.js';

export type EdgeTab = 'bacnet' | 'mbus' | 'knx' | 'firewall';

const TABS: [EdgeTab, string, string][] = [
  ['bacnet', 'BACnet', 'objects'],
  ['mbus', 'M-Bus', 'mbus'],
  ['knx', 'KNX', 'knx'],
  // The host firewall (ADR 0009), not a protocol, but per edge as well.
  ['firewall', 'Firewall', 'firewall'],
];

export function EdgeTabs({ edgeId, active }: { edgeId: string; active: EdgeTab }) {
  return (
    <nav className="tabs" aria-label="Protocols and firewall of this edge">
      {TABS.map(([tab, label, path]) => (
        <Link
          key={tab}
          to={`/edges/${encodeURIComponent(edgeId)}/${path}`}
          className={tab === active ? 'tab active' : 'tab'}
          aria-current={tab === active ? 'page' : undefined}
        >
          {label}
        </Link>
      ))}
    </nav>
  );
}

/** Where the draft stands, with Apply and Release. */
export function BundleBar({
  managed,
  bundle,
  busy,
  onApply,
  onRelease,
}: {
  managed: boolean;
  bundle: BundleState;
  busy: boolean;
  onApply: () => void;
  onRelease: () => void;
}) {
  const s = bundleSummary(managed, bundle);
  return (
    <div className="bundle-bar">
      <span className={`badge badge-config-${s.badge}`}>
        {s.badge === 'local' ? 'Edge file' : s.badge}
      </span>
      <span>{s.text}</span>
      {bundle.rejected && (
        <span className="bad">
          Version {bundle.rejected.version} was rejected: {bundle.rejected.error ?? 'no reason'}
        </span>
      )}
      <span className="bundle-bar-actions">
        {managed && (
          <button
            type="button"
            className="btn btn-small btn-danger-outline"
            disabled={busy}
            title="The edge's local file configures this protocol again after the next Apply"
            onClick={onRelease}
          >
            Release to edge file
          </button>
        )}
        <button
          type="button"
          className="btn btn-small btn-primary"
          disabled={busy || !bundle.pending}
          onClick={onApply}
        >
          Apply
        </button>
      </span>
    </div>
  );
}

/** Runs page actions one at a time; stale drafts become a notice, the rest an error. */
export function useAction(reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const run = useCallback(
    async (action: () => Promise<unknown>, done?: string): Promise<boolean> => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await action();
        if (done) setNotice(done);
        return true;
      } catch (e) {
        if (isStaleConfig(e)) setNotice(staleConfigMessage);
        else setError(e instanceof Error ? e : new Error(String(e)));
        return false;
      } finally {
        setBusy(false);
        reload();
      }
    },
    [reload],
  );
  const view = (
    <>
      {notice && (
        <div className="alert alert-info" role="status">
          <span>{notice}</span>
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              setNotice(null);
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      {error && <ErrorBox error={error} />}
    </>
  );
  return { busy, run, view };
}
